import type { Route } from "@/components/nav-main";
import {
  Activity,
  Award,
  BadgePercent,
  BoxesIcon,
  Building2,
  CalendarClock,
  Carrot,
  ChartAreaIcon,
  ChartPieIcon,
  Computer,
  CreditCard,
  Euro,
  FileBoxIcon,
  FileText,
  GanttChart,
  LayoutDashboard,
  LayoutGrid,
  LucideArrowDown01,
  LucideMenu,
  Monitor,
  Package,
  Palette,
  Plug,
  ScatterChartIcon,
  Settings,
  Shield,
  ShoppingCart,
  Store,
  Tablet,
  TrendingUp,
  User,
  UserCog2,
  Users,
  UtensilsCrossed,
  Wallet,
} from "lucide-react";
import { Role } from "../../api/custom";

export interface NavigationItem {
  title: string;
  url: string;
  icon: React.ReactElement;
  isExternal?: boolean;
  keywords?: string[];
  isDisabled?: boolean;
  requiredRole?: Role;
}

export interface NavigationSection {
  title: string;
  items: NavigationItem[];
  requiredRole?: Role;
}

export const navigation: NavigationSection[] = [
  {
    title: "Dashboard",
    items: [
      {
        title: "Explore",
        url: "/",
        icon: <LayoutDashboard />,
        keywords: ["home", "overview", "dashboard", "welcome", "explore"],
      },
      {
        title: "Total Sales",
        url: "/dashboard",
        icon: <Euro />,
        keywords: ["sales", "revenue", "money", "dashboard"],
      },
      {
        title: "Total Orders",
        url: "/orders",
        icon: <ShoppingCart />,
        keywords: ["orders", "transactions", "dashboard"],
      },
      {
        title: "Sales Overview",
        url: "/sales-overview",
        icon: <GanttChart />,
        keywords: ["overview", "sales", "dashboard"],
      },
    ],
  },
  {
    title: "Sales",
    items: [
      {
        title: "Latest Sales",
        url: "/latest-sales",
        icon: <ChartAreaIcon />,
        keywords: ["latest", "recent", "sales"],
      },
      {
        title: "POS Sales",
        url: "/pos-sales",
        icon: <Computer />,
        keywords: ["pos", "point of sale", "sales"],
      },
      {
        title: "Munchi Online & Kiosk Sales",
        url: "/munchi-sales",
        icon: <UtensilsCrossed />,
        keywords: ["munchi", "online", "kiosk", "sales"],
      },
      {
        title: "POS Tax Breakdown",
        url: "/pos-tax",
        icon: <LucideArrowDown01 />,
        keywords: ["pos", "tax", "breakdown"],
      },
      {
        title: "Online & Kiosk Tax Breakdown",
        url: "/munchi-tax",
        icon: <LucideArrowDown01 />,
        keywords: ["munchi", "tax", "breakdown", "online", "kiosk"],
      },
      {
        title: "Transaction History",
        url: "/orders/history",
        icon: <ShoppingCart />,
        keywords: ["orders", "transactions", "history", "pos", "online"],
      },
    ],
  },
  {
    title: "Analytics",
    items: [
      {
        title: "Sales Pressure",
        url: "/pressure",
        icon: <ScatterChartIcon />,
        keywords: ["pressure", "analytics", "sales"],
      },
      {
        title: "Status Breakdown",
        url: "/breakdown",
        icon: <ChartPieIcon />,
        keywords: ["breakdown", "analytics", "status"],
      },
      {
        title: "Product Performance",
        url: "/product-performance",
        icon: <FileBoxIcon />,
        keywords: ["product", "performance", "analytics"],
      },
    ],
  },
  {
    title: "My Venues",
    items: [
      {
        title: "Stores",
        url: "https://dashboard.gomunchi.com/stores/list?active=true&page=1&pageSize=10",
        isExternal: true,
        icon: <Store />,
        keywords: ["stores", "venues", "locations"],
      },
      {
        title: "Products",
        url: "https://dashboard.gomunchi.com/products",
        isExternal: true,
        icon: <Package />,
        keywords: ["products", "items", "inventory"],
      },
      {
        title: "Menus",
        url: "https://dashboard.gomunchi.com/menus",
        isExternal: true,
        icon: <LucideMenu />,
        keywords: ["menus", "menu items"],
      },
      {
        title: "Inventory",
        url: "/venues/inventory",
        icon: <BoxesIcon />,
        keywords: ["inventory", "stock", "analytics", "quantity"],
      },
      {
        title: "Ingredient",
        url: "https://dashboard.gomunchi.com/inventory",
        isExternal: true,
        icon: <Carrot />,
        keywords: ["ingredient", "ingredients", "food", "materials"],
      },
    ],
  },
  {
    title: "Reports",
    items: [
      {
        title: "Sales Report",
        url: "/reports/sales",
        icon: <FileText />,
        keywords: ["reports", "sales", "documents", "exports", "pos", "kiosk", "storefront", "mobile"],
      },
      /* Munchi Online tab removed — superseded by the unified Sales Report (source selector).
      {
        title: "Munchi Online",
        url: "/reports/munchi-online",
        icon: <FileText />,
        keywords: ["reports", "sales", "online", "storefront", "munchi"],
      },
      */
    ],
  },
  {
    title: "Staffs",
    items: [
      {
        title: "Manage",
        url: "/users/list",
        icon: <Users />,
        keywords: ["manage", "staffs", "team", "people"],
      },
      {
        title: "Activity",
        url: "/users/activity",
        icon: <Activity />,
        keywords: ["activity", "logs", "history"],
      },
      {
        title: "Roles",
        url: "/users/roles",
        icon: <Shield />,
        keywords: ["roles", "permissions", "security", "access control"],
      },
      {
        title: "Shifts",
        url: "/users/shifts",
        icon: <CalendarClock />,
        keywords: ["shifts", "schedule", "time", "clock"],
      },
    ],
  },
  {
    title: "Customers",
    items: [
      {
        title: "Loyalty",
        url: "/customers/analytics",
        icon: <Award />,
        keywords: ["customer", "loyalty", "rewards", "365", "channels"],
      },
      {
        title: "Companies",
        url: "/customers/company",
        icon: <Building2 />,
        keywords: ["company", "companies", "business", "organization"],
      },
    ],
  },
  {
    title: "Management",
    items: [
      {
        title: "POS",
        url: "#",
        icon: <Computer />,
        keywords: ["pos", "point of sale"],
        isDisabled: true,
      },
      {
        title: "Storefront & QR",
        url: "#",
        icon: <Store />,
        keywords: ["storefront", "online", "ordering", "qr"],
        isDisabled: true,
      },
      {
        title: "Kiosk",
        url: "/management/kiosks",
        icon: <Monitor />,
        keywords: ["kiosk", "self-service", "kiosk pro"],
      },
      {
        title: "Kiosk Devices",
        url: "/management/kiosks/devices",
        icon: <Monitor />,
        keywords: ["kiosk", "devices", "device management", "hardware", "kiosk devices"],
      },
      {
        title: "Kiosk Theme",
        url: "/management/kiosks/theme",
        icon: <Palette />,
        keywords: ["kiosk", "theme", "appearance", "look", "feel", "branding", "customize"],
      },
      {
        title: "Loyalty",
        url: "#",
        icon: <Award />,
        keywords: ["loyalty", "rewards", "loyalty program"],
        isDisabled: true,
      },
      {
        title: "Munchi Pay",
        url: "#",
        icon: <CreditCard />,
        keywords: ["payment", "terminal", "card", "munchi pay"],
        isDisabled: true,
      },
      {
        title: "Integration",
        url: "#",
        icon: <BoxesIcon />,
        keywords: ["integration", "order", "integration"],
        isDisabled: true,
      },
    ],
  },
  {
    title: "Operations",
    requiredRole: Role.MUNCHI_ADMIN,
    items: [
      {
        title: "Munchi <> Viva Rate",
        url: "/operations/payment-rate",
        icon: <CreditCard />,
        keywords: ["payment", "rate", "viva", "operations", "transaction", "munchi"],
      },
    ],
  },
];

export const routes: Route[] = [
  {
    id: "dashboard",
    title: "Dashboard",
    icon: <LayoutDashboard className="size-4" />,
    link: "/",
    subs: [
      {
        title: "Explore",
        link: "/",
        icon: <LayoutDashboard className="size-4" />,
      },
      {
        title: "Total Sales",
        link: "/dashboard",
        icon: <Euro className="size-4" />,
      },
      {
        title: "Total Orders",
        link: "/orders",
        icon: <ShoppingCart className="size-4" />,
      },
      {
        title: "Sales Overview",
        link: "/sales-overview",
        icon: <GanttChart className="size-4" />,
      },
    ],
  },
  {
    id: "sales",
    title: "Sales",
    icon: <TrendingUp className="size-4" />,
    link: "/latest-sales",
    subs: [
      {
        title: "Latest Sales",
        link: "/latest-sales",
        icon: <ChartAreaIcon className="size-4" />,
      },
      {
        title: "POS Sales",
        link: "/pos-sales",
        icon: <Computer className="size-4" />,
      },
      {
        title: "Munchi Online & Kiosk",
        link: "/munchi-sales",
        icon: <UtensilsCrossed className="size-4" />,
      },
      {
        title: "POS Tax Breakdown",
        link: "/pos-tax",
        icon: <LucideArrowDown01 className="size-4" />,
      },
      {
        title: "Online & Kiosk Tax",
        link: "/munchi-tax",
        icon: <LucideArrowDown01 className="size-4" />,
      },
      {
        title: "Transaction History",
        link: "/orders/history",
        icon: <ShoppingCart className="size-4" />,
      },
    ],
  },
  {
    id: "analytics",
    title: "Analytics",
    icon: <ChartPieIcon className="size-4" />,
    link: "/pressure",
    subs: [
      {
        title: "Sales Pressure",
        link: "/pressure",
        icon: <ScatterChartIcon className="size-4" />,
      },
      {
        title: "Status Breakdown",
        link: "/breakdown",
        icon: <ChartPieIcon className="size-4" />,
      },
      {
        title: "Product Performance",
        link: "/product-performance",
        icon: <FileBoxIcon className="size-4" />,
      },
    ],
  },
  {
    id: "venues",
    title: "My Venues",
    icon: <Store className="size-4" />,
    link: "https://dashboard.gomunchi.com/stores/list?active=true&page=1&pageSize=10",
    isExternal: true,
    subs: [
      {
        title: "Stores",
        link: "https://dashboard.gomunchi.com/stores/list?active=true&page=1&pageSize=10",
        icon: <Store className="size-4" />,
        isExternal: true,
      },
      {
        title: "Products",
        link: "https://dashboard.gomunchi.com/products",
        icon: <Package className="size-4" />,
        isExternal: true,
      },
      {
        title: "Menus",
        link: "https://dashboard.gomunchi.com/menus",
        icon: <LucideMenu className="size-4" />,
        isExternal: true,
      },
      {
        title: "Inventory",
        link: "/venues/inventory",
        icon: <BoxesIcon />,
      },
      {
        title: "Ingredient",
        link: "https://dashboard.gomunchi.com/inventory",
        icon: <Carrot className="size-4" />,
        isExternal: true,
      },
    ],
  },
  {
    id: "reports",
    title: "Reports",
    icon: <FileText className="size-4" />,
    link: "/reports/sales",
    subs: [
      {
        title: "Sales Report",
        link: "/reports/sales",
        icon: <FileText className="size-4" />,
      },
      /* Munchi Online tab removed — superseded by the unified Sales Report (source selector).
      {
        title: "Munchi Online",
        link: "/reports/munchi-online",
        icon: <FileText className="size-4" />,
      },
      */
    ],
  },
  {
    id: "users",
    title: "Staffs",
    icon: <Users className="size-4" />,
    link: "/users",
    subs: [
      {
        title: "Manage",
        link: "/users/list",
        icon: <Users className="size-4" />,
      },
      {
        title: "Activity",
        link: "/users/activity",
        icon: <Activity className="size-4" />,
      },
      {
        title: "Roles",
        link: "/users/roles",
        icon: <Shield className="size-4" />,
      },
      {
        title: "Shifts",
        link: "/users/shifts",
        icon: <CalendarClock className="size-4" />,
      },
    ],
  },
  {
    id: "customer",
    title: "Customers",
    icon: <User className="size-4" />,
    link: "/customers/analytics",
    isNew: true,
    subs: [
      {
        title: "Loyalty",
        link: "/customers/analytics",
        icon: <Award className="size-4" />,
      },
      {
        title: "Companies",
        link: "/customers/company",
        icon: <Building2 className="size-4" />,
      },
    ],
  },
  {
    id: "management",
    title: "Management",
    icon: <Settings className="size-4" />,
    link: "/management",
    isNew: true,
    subs: [
      {
        title: "All",
        link: "/management",
        icon: <LayoutGrid className="size-4" />,
      },
      {
        title: "Storefront & QR",
        link: "#",
        icon: <Store className="size-4" />,
        isDisabled: true,
      },
      {
        title: "Kiosk",
        link: "/management/kiosks",
        icon: <Tablet className="size-4" />,
      },
      {
        title: "POS",
        link: "#",
        icon: <Computer className="size-4" />,
        isDisabled: true,
      },
      {
        title: "Loyalty",
        link: "#",
        icon: <Award className="size-4" />,
        isDisabled: true,
      },
      {
        title: "Munchi Pay",
        link: "#",
        icon: <Wallet className="size-4" />,
        isDisabled: true,
      },
      {
        title: "Integration",
        link: "#",
        icon: <Plug className="size-4" />,
        isDisabled: true,
      },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    icon: <UserCog2 className="size-4" />,
    link: "/operations/payment-rate",
    requiredRole: Role.MUNCHI_ADMIN,
    subs: [
      {
        title: "Munchi <> Viva Rate",
        link: "/operations/payment-rate",
        icon: <BadgePercent className="size-4" />,
      },
    ],
  },
];
